import { type ChangeEvent, useContext, useMemo, useState } from 'react';

import { MagnifyingGlass } from '@stratumn/icons';
import debounce from 'debounce';
import shortid from 'shortid';

import { cn } from '@/shadcn/lib/utils';

import type { SearchBarConfig } from './search.type';
import { SearchContext } from './searchContext';

type Props = {
  config: SearchBarConfig;
};

export const SearchBar = ({ config }: Props) => {
  const searchContext = useContext(SearchContext);
  const { state: searchState, dispatch } = searchContext || {};
  const searchString = searchState?.string || '';

  const handlerDispatch = (payload: string) => {
    if (dispatch) {
      dispatch({ type: 'addString', payload });
    }
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) =>
    debounce(handlerDispatch(event.target.value), 500);

  const [hasFocus, setHasFocus] = useState(false);

  const searchId = useMemo(() => shortid.generate(), []);

  const placeholder = config?.placeholder || 'Search';

  return (
    <label
      className={cn(
        'flex h-7 max-w-[540px] grow items-center overflow-hidden rounded-md border border-border bg-card pl-2 text-card-foreground transition-colors duration-100',
        hasFocus && 'border-primary'
      )}
      htmlFor={searchId}
    >
      <MagnifyingGlass className="mr-2 size-4 fill-current" />
      <input
        className="mr-2 grow border-0 bg-card px-3 font-normal text-card-foreground text-xs placeholder:text-muted-foreground focus:outline-hidden focus:ring-0"
        id={searchId}
        onBlur={() => setHasFocus(false)}
        onChange={handleSearch}
        onFocus={() => setHasFocus(true)}
        placeholder={placeholder}
        value={searchString}
      />
    </label>
  );
};
